export enum CampaignStatus {
  Draft = 1,
  Scheduled = 2,
  Active = 3,
  EntryClosed = 4,
  DrawInProgress = 5,
  Completed = 6,
  Cancelled = 7,
}

export interface CampaignPrize {
  id: string;
  tierName: string;
  amount: number;
  currency: string;
  quantity: number;
  tierOrder: number;
  prizeType?: 'digital_asset' | 'voucher' | 'other' | number | string | null;
}

export interface CampaignParticipation {
  hasJoined: boolean;
  entryWeight?: number | null;
  isWinner: boolean;
  prizeId?: string | null;
  joinedAt?: string | null;
  cardPreference?: string | null;
  isQualified?: boolean | null;
  qualifiedAt?: string | null;
}

export interface CampaignLegalDocuments {
  termsAndConditions?: string | null;
  privacyPolicy?: string | null;
  termsRequireAcknowledgement?: boolean;
  privacyRequireAcknowledgement?: boolean;
  /** True when any configured document requires confirmation before joining. */
  requireAcknowledgement?: boolean;
  contentFormat?: 'markdown' | 'plain';
}

export interface CampaignEmbeddedVideo {
  title?: string | null;
  embedUrl: string;
  provider: string;
}

export interface CampaignDetail {
  id: string;
  slug: string;
  title: string;
  description: string;
  rulesConfig: string;
  drawType: number;
  status: CampaignStatus;
  entryStartsAt: string;
  entryEndsAt: string;
  drawScheduledAt?: string | null;
  participantCount: number;
  qualifiedParticipantCount: number;
  prizes: CampaignPrize[];
  legalDocuments?: CampaignLegalDocuments | null;
  embeddedVideos?: CampaignEmbeddedVideo[];
  participation?: CampaignParticipation | null;
}

export interface JoinCampaignResult {
  campaignId: string;
  participantId: string;
  entryWeight: number;
  joinedAt: string;
  alreadyJoined: boolean;
  isQualified: boolean;
  cardPreference?: string | null;
}

export interface CampaignMyResult {
  campaignId: string;
  campaignStatus: CampaignStatus;
  hasJoined: boolean;
  entryWeight: number;
  isWinner: boolean;
  prizeId?: string | null;
  wonPrize?: CampaignPrize | null;
  joinedAt: string;
  isQualified?: boolean;
  cardPreference?: string | null;
}

export const campaignKeys = {
  all: ['campaigns'] as const,
  bySlug: (slug: string) => [...campaignKeys.all, 'slug', slug] as const,
  winnersBySlug: (slug: string) => [...campaignKeys.all, 'slug', slug, 'winners'] as const,
  myResult: (id: string) => [...campaignKeys.all, id, 'my-result'] as const,
  myParticipations: () => [...campaignKeys.all, 'mine'] as const,
  myActivity: (id: string) => [...campaignKeys.all, id, 'my-activity'] as const,
  config: () => [...campaignKeys.all, 'config'] as const,
  list: () => [...campaignKeys.all, 'list'] as const,
};

export interface CampaignPrizeWinnerSummary {
  prizeId: string;
  prizeName: string;
  prizeAmount: number;
  prizeCurrency: string;
  tierOrder: number;
  winnerCount: number;
  winnerCoreIds?: string[];
}

export interface CampaignWinnersResponse {
  campaignId: string;
  winners: CampaignPrizeWinnerSummary[];
}

export interface CampaignConfig {
  competitionsEnabled: boolean;
  hasVisibleCompetitions: boolean;
}

export interface CampaignSummary {
  id: string;
  slug: string;
  title: string;
  description: string;
  status: CampaignStatus;
  entryStartsAt: string;
  entryEndsAt: string;
  participantCount: number;
  qualifiedParticipantCount: number;
  prizes?: CampaignPrize[];
}

export interface JoinCampaignParams {
  campaignId: string;
  marketingChannel?: string | null;
  cardPreference?: string | null;
  acceptedLegalTerms?: boolean;
}

export interface CampaignMyParticipation {
  campaignId: string;
  participantId: string;
  joinedAt: string;
  isQualified: boolean;
  qualifiedAt?: string | null;
  isWinner: boolean;
  prizeId?: string | null;
  fulfillmentStatus: string;
  disqualificationComment?: string | null;
  winnerStatus?: string | null;
  campaignSlug?: string | null;
  campaignTitle?: string | null;
  campaignStatus?: CampaignStatus | number | null;
}

export interface CampaignParticipantActivityEvent {
  id: string;
  campaignId: string;
  participantId: string;
  eventType: string;
  occurredAt: string;
  actorType: string;
  payloadJson: string;
}

export interface CampaignMyActivity {
  campaignId: string;
  participantId: string;
  events: CampaignParticipantActivityEvent[];
}
